import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./Layout.scss";
import { Footer } from "../Footer/Footer";
import { Header } from "../Header/Header";
import Loading from "../__common/Loading/Loading";
import { useI18Next } from "../../i18next";
import { ScrollToTop } from "../__common/ScrollToTop/ScrollToTop";
import { useUserHooks } from "../../hooks/user-hooks";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { EuCookieAlert, NonEuCookieAlert } from "../CookieModal/CookieModal";
import { UserState } from "../../store/reducers/user-reducer";
import { Cookie, LocalStorage } from "../../services/storage";
import {
  COOKIE_AD_ATKN,
  EMAIL_LOGIN_VERIFICATION_RESULT,
  REDIRECT,
  REDIRECT_URI,
  SHOW_TIMEOUT_DIALOG,
} from "../../constants/storage";
import { useQuery } from "../../hooks/query-hooks";
import { HelpTipsPoupup } from "../HelpTipsPopup/HelpTipsPopup";
import { ROUTES } from "../../configs/routes";
import { useLayout } from "../../hooks/layout-hooks";
import { UtilsBrowser } from "../../utils/UtilsBrowser";
import classNames from "classnames";
import { URLS } from "../../constants/urls";
import { LoginModal } from "../LoginModal/LoginModal";
import { EmailLoginModal } from "../EmailLoginModal/EmailLoginModal";
import { OTPModal } from "../OTPModal/OTPModal";
import { useLoginOptionHooks } from "../../hooks/login-option-hooks";
import { LoginOptionState } from "../../store/reducers/login-option-reducer";
import { routeShouldRenderComponent } from "../../utils/UtilsLayout";
import {
  ROUTES_NO_COOKIE_NOTICE,
  ROUTES_NO_HEADER_AND_FOOTER,
  ROUTES_NO_HELP_TIPS,
} from "../../configs/layout";
import FocusTrapping from "../__hoc/FocusTrapping";
import { UtilsUseHistory } from "../../utils/UtilsUseHistory";
import { API_BASE_URL } from "../../constants/api";
import { UtilsUser } from "../../utils/UtilsUser";
import { LayoutState } from "../../store/reducers/layout-reducer";
import { SystemState } from "../../store/reducers/site-reducer";
import { useLocale } from "../../hooks/locale-hooks";
import { useAuth } from "../../hooks/auth-hooks";
import {
  DISABLED_FEATURES,
  useFeatureToggle,
} from "../../utils/feature-toggle/useFeatureToggle";
import { useSiteSettingsHook } from "../../hooks/site-settings-hooks";
import GoogleAnalytics from "../../utils/GoogleAnalytics";
import { ErrorMaintenance } from "../../pages/nonauth/ErrorPages/ErrorMaintenance";
import { useAdssoLoginGuard } from "../../hooks/adsso-login-guard-hooks";
import { TURKEY_SA_COUNTRY_CODE } from "../../constants/countryDefaults";
import { useEmailSupport } from "../../utils/email-supported/useEmailSupport";
import { IdleTimeoutDialog } from "../__common/dialog/IdleTimeoutDialog/idleTimeoutDialog";
import { useAuthRouteLoginHandler } from "../../hooks/auth-login-route-hooks";

interface Props {
  children: React.ReactNode;
}

export const Layout: React.FC<Props> = (props) => {
  // third party hooks
  const history = useHistory();
  const { pathname } = useLocation();
  const { i18next } = useI18Next();

  // custom hooks
  const query = useQuery();
  const { logout } = useAuth();
  const { getUserData, loadUserCountryCode, isDigitalLegacy } = useUserHooks();
  const { getUserCountry, setSelectedCountry, removeSelectedCountry } =
    useLayout();
  const {
    toggleLoginModal,
    toggleEmailLoginModal,
    toggleEmailOtpModal,
    removeEmailLoginSession,
    removeRedirectSession,
  } = useLoginOptionHooks();
  const { isDisabled } = useFeatureToggle();

  const { loadSystemProperties } = useSiteSettingsHook();
  const { ssoLoginRedirect } = useAdssoLoginGuard();
  const { isEmailLoginSupported } = useEmailSupport();

  // states
  const { userCountryCode, isGeoIPLoading, userData, isGetUserDataLoading } =
    useSelector<ApplicationState, UserState>((state) => state.user);
  const { isLoginModalOpen, isEmailLoginModalOpen, isEmailOtpModalOpen } =
    useSelector<ApplicationState, LoginOptionState>(
      (state) => state.loginOption
    );
  const { selectedCountry } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const { isLoading: siteSettingIsLoading } = useSelector<
    ApplicationState,
    SystemState
  >((state) => state.system);

  // variables
  const redirectUri = Cookie.get(REDIRECT_URI);
  const timeOutDialog = localStorage.getItem(SHOW_TIMEOUT_DIALOG);
  const isTimeOutDialogOpen = timeOutDialog === "true";
  const { go } = UtilsUseHistory();
  const isEmailLoginOngoing = isEmailLoginModalOpen || isEmailOtpModalOpen;
  const isLoginAttemptOngoing =
    isEmailLoginModalOpen || isEmailOtpModalOpen || isLoginModalOpen;
  const redirectToChinaLocalSite =
    userCountryCode === "CN" &&
    !UtilsUser.isUserLoggedIn() &&
    pathname !== ROUTES.DIGITAL_LEGACY_AUTH;

  const handleChinaGeoipRedirect = () => {
    if (UtilsUser.isUserLoggedIn() && isDigitalLegacy) return;

    if (redirectToChinaLocalSite) {
      window.location.replace(URLS.CHINA_LOCALSITE);
    }
  };

  const isHomePage = pathname === ROUTES.ROOT;

  useEffect(() => {
    if (
      !isDisabled(DISABLED_FEATURES.ADSSO_SITE_RESTRICTION) &&
      !Cookie.get(COOKIE_AD_ATKN)
    ) {
      ssoLoginRedirect();
    }

    if (
      !isDisabled(DISABLED_FEATURES.UNDER_MAINTENANCE) &&
      pathname !== ROUTES.ERROR_MAINTENANCE
    ) {
      window.location.replace(ROUTES.ERROR_MAINTENANCE);
    }

    // Redirect to designated page after logging in
    if (redirectUri && query.get("success")) {
      window.location.replace(`${API_BASE_URL}${Cookie.get(REDIRECT_URI)}`);
      Cookie.remove(REDIRECT_URI);
    }

    if (pathname === ROUTES.OLD_ABOUT_GRPS) {
      history.replace(ROUTES.ABOUT_GRPS);
    }
    if (
      userData &&
      userData.countryCode === TURKEY_SA_COUNTRY_CODE &&
      UtilsUser.isUserLoggedIn()
    ) {
      removeSelectedCountry();
    }
    if (!isDisabled(DISABLED_FEATURES.SITE_SETTINGS)) loadSystemProperties();
  }, []);

  useEffect(() => {
    if (!userCountryCode) return;
    handleChinaGeoipRedirect();

    const country = getUserCountry();
    setSelectedCountry(country);
  }, [userCountryCode]);

  useEffect(() => {
    if (siteSettingIsLoading) return;

    loadUserCountryCode();
    removeEmailLoginSession();

    // Validate session: after login, page reload, or initial load
    getUserData();
  }, [siteSettingIsLoading]);

  // useEffect(() => {
  //   // Send page views to Google Analytics
  //   // TODO: add condition to avoid initializing the GA feature in local environment
  //   GoogleAnalytics.initialize(window.location.host);
  //   if (GoogleAnalytics.isInitialized) GoogleAnalytics.sendPageView(pathname);
  // }, [pathname]);

  useEffect(() => {
    window.onpopstate = () =>
      setTimeout(function () {
        if (isEmailLoginOngoing || isLoginModalOpen) {
          toggleEmailOtpModal(false);
          toggleLoginModal({ isLoginModalOpen: false });
          toggleEmailLoginModal(false);
          go(-1);
        }
      }, 0);
  }, [isEmailLoginOngoing, isLoginModalOpen]);

  useEffect(
    () => {
      const emailLoginVerificationResult = LocalStorage.get(
        EMAIL_LOGIN_VERIFICATION_RESULT
      );
      const isVerifying =
        emailLoginVerificationResult &&
        emailLoginVerificationResult === "false";

      // If email login verification was abandoned (modal closed), clear the flag
      if (isVerifying && !isEmailLoginOngoing) {
        removeEmailLoginSession();
      }
    },
    // eslint-disable-next-line
    [isEmailLoginOngoing]
  );

  useEffect(() => {
    if (!isLoginAttemptOngoing) {
      removeRedirectSession();
    }
  }, [isLoginAttemptOngoing]);

  // Check if user is redirected back to home page after attempting
  // to directly access an authenticated route (deep link)
  useEffect(() => {
    if (!selectedCountry.code || !userCountryCode) return;
    // CHECK IF THE NEW selectedCountry doesn't support email login
    if (
      UtilsUser.isEmailLogin() &&
      !isEmailLoginSupported(selectedCountry.code)
    ) {
      // Logout user: backend clears HttpOnly cookies, frontend clears session state
      logout();
      sessionStorage.setItem(REDIRECT, ROUTES.MYDATA);
    }
  }, [userCountryCode, selectedCountry]);

  useLocale();
  useAuthRouteLoginHandler();

  // Show loading spinner while fetching user data (e.g., after SA login redirect)
  const saLoginRedirect = LocalStorage.get("SA_LOGIN_REDIRECT");
  if (
    (isGetUserDataLoading && !userData?.countryCode) ||
    saLoginRedirect === "true"
  ) {
    return (
      <div className="body" data-testid="layout">
        <Loading />
      </div>
    );
  }

  return i18next.isInitialized && !redirectToChinaLocalSite ? (
    <div className="body" data-testid="layout">
      {<IdleTimeoutDialog isTimeOutDialogOpen={isTimeOutDialogOpen} />}
      {!UtilsBrowser.isInternetExplorer() &&
        !routeShouldRenderComponent(pathname, ROUTES_NO_HEADER_AND_FOOTER) && (
          <Header />
        )}
      {isDisabled(DISABLED_FEATURES.UNDER_MAINTENANCE) &&
        !UtilsBrowser.isInternetExplorer() &&
        !isGeoIPLoading &&
        !routeShouldRenderComponent(pathname, ROUTES_NO_COOKIE_NOTICE) && (
          <>
            <NonEuCookieAlert />
            <EuCookieAlert />
          </>
        )}

      {!UtilsUser.isUserLoggedIn() && (
        <FocusTrapping
          onHandleClose={() => toggleLoginModal({ isLoginModalOpen: false })}
          isActive={isLoginModalOpen}
          modalId="login-modal"
        >
          <LoginModal
            headertestid="loginmodal-onclose-button"
            show={isLoginModalOpen}
            onClose={() => {
              toggleLoginModal({ isLoginModalOpen: false });
            }}
          />
        </FocusTrapping>
      )}

      {isEmailLoginModalOpen && (
        <FocusTrapping
          onHandleClose={() => toggleEmailLoginModal(false)}
          isActive={isEmailLoginModalOpen}
          modalId="email-login-modal"
        >
          <EmailLoginModal
            isOpen={isEmailLoginModalOpen}
            setIsOpen={toggleEmailLoginModal}
          />
        </FocusTrapping>
      )}

      {isEmailOtpModalOpen && (
        <FocusTrapping
          onHandleClose={() => toggleEmailOtpModal(false)}
          isActive={isEmailOtpModalOpen}
          modalId="otp-modal"
        >
          <OTPModal
            isOpen={isEmailOtpModalOpen}
            setIsOpen={toggleEmailOtpModal}
          />
        </FocusTrapping>
      )}

      <main
        id="main-body"
        className={classNames({
          "snap-scroll": isHomePage,
        })}
      >
        {!UtilsBrowser.isInternetExplorer() &&
          !routeShouldRenderComponent(pathname, ROUTES_NO_HELP_TIPS) && (
            <HelpTipsPoupup />
          )}

        <div className="layout" id="layout-container">
          {isDisabled(DISABLED_FEATURES.UNDER_MAINTENANCE) ? (
            props.children
          ) : (
            <ErrorMaintenance />
          )}
        </div>
        <ScrollToTop />
        {!UtilsBrowser.isInternetExplorer() &&
          !routeShouldRenderComponent(pathname, [ROUTES.ADSSO_LOGOUT]) &&
          isDisabled(DISABLED_FEATURES.UNDER_MAINTENANCE) && <Footer />}
      </main>
    </div>
  ) : (
    <div data-testid="No-access"></div>
  );
};
